import * as React from "react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { Check, Minus } from "@phosphor-icons/react"

import { cn } from "@/lib/utils"
import { useFieldControl } from "@/components/ds/Field"

/**
 * A 20px square with a 4px radius and a tick: never round, so it cannot be
 * taken for a radio (rounded-sm was 8px on a 16px box, a circle). Ink when
 * ticked (red is the screen's one commitment action, not a selection), the
 * field edge (#8A94A6, 3.06:1) when not, red when invalid. The hit area
 * reaches 28px around the box; put it in a <label> (or use CheckboxField)
 * for a full-row target. Focus is the site-wide ring.
 */
const Checkbox = React.forwardRef(({ className, ...props }, ref) => {
  const control = useFieldControl(props)
  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        "group/checkbox peer relative inline-flex size-5 shrink-0 items-center justify-center rounded-xs border-[1.5px] border-touki-field bg-white text-white",
        "transition-[background-color,border-color] duration-fast ease-tk-out",
        "after:absolute after:-inset-1 after:content-['']",
        "hover:border-touki-black",
        "data-[state=checked]:border-touki-dark data-[state=checked]:bg-touki-dark",
        "data-[state=indeterminate]:border-touki-dark data-[state=indeterminate]:bg-touki-dark",
        "aria-[invalid=true]:border-touki-red",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...control}>
      <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
        <Check weight="bold" aria-hidden="true" className="size-3.5 group-data-[state=indeterminate]/checkbox:hidden" />
        <Minus weight="bold" aria-hidden="true" className="hidden size-3.5 group-data-[state=indeterminate]/checkbox:block" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
})
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export { Checkbox }
